import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import "./GeneratedModels.css";
import { useDispatch, useSelector } from 'react-redux';
import PendingStatusModal from './PendingStatusModal';
import WorkFlowPageLinkDeciderModal from './WorkFlowPageLinkDeciderModal';
import { useGetRedirectionURLMutation, useLazyDashboardGeneratedModelListQuery } from '../../../../service/apiSlices/dashboard';
import { getModelListParams, renderEmptyState, renderModelCard, useModelOperations } from '../../../../utilities/Models/modelUtils';
import { editModel, setSelectedKeys, setSelectModel, updateInitialGeneratedModels } from '../../../../service/apiSlices/staticApi';
import { GeneratedModelsProps, GeneratedModelTypes } from '../../../../utilities/types/ModelsTypes';
import { useScrollContainer } from '../../../../utilities/utils';
import { isEmptyArray } from 'formik';

const GeneratedModels: React.FC<GeneratedModelsProps> = ({
  type,
  activeFilter,
  isActive,
  search,
  getGeneratedModelCount
}) => {
  const { countGeneratedModels, generatedList: models, selectModel } = useSelector((state: any) => state.static);
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);
  const [selectModelKeys, setSelectModelKeys] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 24;

  const [triggerGeneratedModelList, { data: modelData, isSuccess }] = useLazyDashboardGeneratedModelListQuery();
  const [getChatURL] = useGetRedirectionURLMutation();
  const { handleModelSelection } = useModelOperations(dispatch);

  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const previousSelectModel = useRef(false);
  // Intersection Observer setup for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        setIsIntersecting(first.isIntersecting);
        if (first.isIntersecting && hasMore && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  useEffect(() => {
    if ((isActive || countGeneratedModels) && !isLoading) {
      setIsLoading(true);
      triggerGeneratedModelList(getModelListParams({
        activeFilter,
        search,
        page,
        limit,
        type
      }))
        .unwrap()
        .then(() => setIsLoading(false))
        .catch(() => {
          setIsLoading(false);
          setHasMore(false);
        });
    }
  }, [activeFilter, isActive, countGeneratedModels, search, page]);

  useEffect(() => {
    if (isSuccess && modelData) {
      const newModels = modelData.data;
      if (page === 1) {
        dispatch(updateInitialGeneratedModels(newModels));
      } else {
        // Prevent duplicates by checking secret_key
        const existingKeys = new Set(models?.map((model: any) => model.secret_key));
        const uniqueNewModels = newModels?.filter((model: any) => !existingKeys.has(model.secret_key));

        if (uniqueNewModels.length > 0) {
          dispatch(updateInitialGeneratedModels([...models, ...uniqueNewModels]));
        }
      }
      getGeneratedModelCount(modelData.total_count);
      setHasMore(newModels.length === limit);
    }
  }, [isSuccess, modelData]);

  useEffect(() => {
    if (!selectModel) {
      setSelectedModuleIds([]);
      setSelectModelKeys([]);
    }
  }, [selectModel]);

  useEffect(() => {
    if (isSuccess && modelData) {
      const newModels = modelData.data;
      dispatch(updateInitialGeneratedModels(newModels));
      getGeneratedModelCount(modelData.total_count);
      setHasMore(newModels.length === limit);
    }
  }, [isSuccess, modelData, dispatch, getGeneratedModelCount]);

  // Reset page when search or filter changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [search, activeFilter]);

  const handleDelete = () => {
    if (isIntersecting && models.length === 0) {
      contentRef.current?.scrollTo(0, 0);
      setPage(1);
      setHasMore(true);
      dispatch(updateInitialGeneratedModels([]));
    }
  };

  useEffect(() => {
    if (selectModel === false && previousSelectModel.current === true) {
      handleDelete();
    }
    previousSelectModel.current = selectModel;
  }, [selectModel]);

  useEffect(() => {
    return () => {
      dispatch(setSelectModel(false));
      dispatch(setSelectedKeys([]));
      previousSelectModel.current = false;
    };
  }, [dispatch]);

  const selectedModel = useMemo(
    () => models?.find((item: GeneratedModelTypes) => item.id === selectedModelId),
    [selectedModelId, models]
  );


  const handleModelClick = (model: GeneratedModelTypes) => {
    setSelectedModelId(model.id);
  };

  const handleModalChange = () => {
    setSelectedModelId(null);
  };

  const handleAllStatusCompleted = (id: string | number, data: GeneratedModelTypes) => {
    dispatch(editModel({ id, data }));
    handleModelClick(data);
  };

  const generateChatLink = (key: string) => {
    getChatURL({ key }).unwrap().then((response) => {
      if (response?.data) {
        window.open(response.data, "_blank");
      }
    });
  };

  const isCompletedModel = selectedModel?.is_model_completed === true;
  const showPendingModal = selectedModel && !isCompletedModel;
  const showWorkFlowPageLinkDeciderModal = selectedModel && isCompletedModel;

  useScrollContainer({ ref: contentRef });

  return (
    <>
      <div className="models-list-container overflow-auto" ref={contentRef}>
        <div className="row modelsList-data-wrapper-row g-3">
          {isEmptyArray(models) && renderEmptyState(activeFilter, search, models?.length)}
          {!isEmptyArray(models) && models?.map((model: any) =>
            renderModelCard({
              model,
              selectModel,
              selectedModuleIds,
              onModelClick: handleModelClick,
              onModelChecked: (id, key) => handleModelSelection(
                id,
                key,
                selectedModuleIds,
                setSelectedModuleIds,
                setSelectModelKeys
              ),
              showPendingStatus: true
            })
          )}
        </div>
        {/* {hasMore && <div ref={loadingRef} className="text-center py-4">Loading more...</div>} */}
      </div>

      {selectedModel && (
        <>
          <PendingStatusModal
            model={selectedModel}
            isVisible={showPendingModal}
            handleOnChange={handleModalChange}
            onAllStatusCompleted={handleAllStatusCompleted}
          />

          <WorkFlowPageLinkDeciderModal
            model={selectedModel}
            isVisible={showWorkFlowPageLinkDeciderModal}
            onChange={handleModalChange}
            onGetURL={generateChatLink}
          />
        </>
      )}
    </>
  );
};

export default memo(GeneratedModels);